import { useEffect, useMemo } from "react";

import { useHistoryDecisionV2 } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { NodeExecutionMetadata } from "src/api/types";
import { BeMappedNode, NodeTestRunStateV2 } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { ResultDataAndAuxRowV2 } from "src/dataTable/types";
import {
  useAuthoringUIActions,
  useSelectedResultsRowDecisionId,
} from "src/flowContainer/AuthoringUIContext";
import { transformDecisionToResultDataRow } from "src/nodeEditor/JobRunIntermediateResultTableWrapper/utils";
import { usePaginatedResults } from "src/nodeEditor/usePaginatedResults";
import { useAuthoringContext } from "src/router/routerContextHooks";

export const useJobRunNodeResults = (
  node: BeMappedNode,
  rowIndex: number,
  partialResult: ResultDataAndAuxRowV2 | undefined,
) => {
  const decisionId = useSelectedResultsRowDecisionId();
  const { workspace } = useAuthoringContext();
  const { enrichSelectedResultsRowMetadata } = useAuthoringUIActions();
  const baseUrl = workspace.base_url ?? "";

  const { data, isFetching } = useHistoryDecisionV2({
    baseUrl,
    decisionId: decisionId ?? "",
    includeNodeResultsData: true,
    iceberg: true,
  });

  const result = useMemo((): ResultDataAndAuxRowV2 | undefined => {
    if (!data && partialResult) return partialResult;
    if (!data || data.isQueryError) return undefined;

    const decision = data.decision;

    return (
      transformDecisionToResultDataRow({
        decision,
        index: rowIndex,
        nodeId: node.id,
      }) ?? undefined
    );
  }, [data, node.id, rowIndex, partialResult]);

  // Enrich the stored row's nodeExecutionMetadata with full decision metadata
  // for tracing (highlights all executed nodes, not just the queried one).
  // Only update metadata to preserve the original row's fields.
  const fullMetadata = useMemo(() => {
    if (!data || data.isQueryError) return undefined;
    return data.decision.node_results.node_execution_metadata as
      | NodeExecutionMetadata
      | undefined;
  }, [data]);

  useEffect(() => {
    if (fullMetadata) {
      enrichSelectedResultsRowMetadata(fullMetadata);
    }
  }, [fullMetadata, enrichSelectedResultsRowMetadata]);

  const decisionError = (() => {
    if (!data?.isQueryError && data?.decision.error) {
      return data.decision.error;
    }

    return undefined;
  })();

  return {
    result,
    isFetching,
    noResultsForThisIndex: !isFetching && !result && !partialResult,
    decisionError,
  };
};

export const useTestRunNodeResults = (
  node: BeMappedNode,
  runState: NodeTestRunStateV2,
  index: number,
) => {
  const isOutputNode = node.type === NODE_TYPE.OUTPUT_NODE;

  const {
    resultsToDisplay: results,
    fetchAdditionalData,
    isFetching,
    additionalDataCanBeFetched,
  } = usePaginatedResults(runState.testResult, isOutputNode, "all");

  const result = results.find((result) => result.index === index);
  const noResultsForThisIndex =
    !result && results.some((result) => result.index > index);

  useEffect(() => {
    if (!isFetching && !result && additionalDataCanBeFetched) {
      fetchAdditionalData();
    }
  }, [
    result,
    index,
    fetchAdditionalData,
    isFetching,
    additionalDataCanBeFetched,
  ]);

  return {
    result,
    isFetching,
    noResultsForThisIndex:
      noResultsForThisIndex || (!additionalDataCanBeFetched && !result),
  };
};
